import { useState, useCallback } from 'react';
import { runRiceTool } from '../../tools/rice';
import { useAttributesStore } from '../../stores/prioritization/attributesStore';
import { ItemInput } from './components/ItemInput';
import { ScoringView } from './components/ScoringView';
import { ResultsView } from './components/ResultsView';
import type { RiceItem } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolMode, type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { LiveSequentialScore } from '../../components/prioritization/session/liveExperience/liveTools/LiveSequentialScore';
import { LiveToolComplete } from '../../components/prioritization/session/liveExperience/liveTools/LiveToolComplete';

type AppState = 'input' | 'scoring' | 'results';

const RICE_PHASE_META: Record<
  'reach' | 'impact' | 'confidence' | 'effort',
  { title: string; description: string; placeholder: string }
> = {
  reach: { title: 'Reach', description: 'How many people/events per quarter?', placeholder: 'e.g. 5000' },
  impact: { title: 'Impact', description: 'Impact score (0.25–3)', placeholder: 'e.g. 2' },
  confidence: { title: 'Confidence', description: 'Confidence % (0–100)', placeholder: 'e.g. 80' },
  effort: { title: 'Effort', description: 'Person-months of effort', placeholder: 'e.g. 3' },
};

function RiceScoringContent(props: ToolHostProps) {
  const toolMode = useToolMode();
  const { isLiveSession, shouldAutoStart, importSourceLabel } = useLiveToolSession();
  const isAppMode = toolMode === 'app';
  const getAttributeByKey = useAttributesStore(state => state.getAttributeByKey);

  const [state, setState] = useState<AppState>('input');
  const [items, setItems] = useState<RiceItem[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [scoringPhase, setScoringPhase] = useState<'reach' | 'impact' | 'confidence' | 'effort'>('reach');
  // Live: the participant pressed Finish on the last item and sees their ranking.
  const [liveDone, setLiveDone] = useState(false);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Handler for importing items from workspace
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newItems: RiceItem[] = toolItems.map((item) => ({
      id: item.id,
      name: item.name,
      reach: null,
      impact: null,
      confidence: null,
      effort: null,
      rice: null,
    }));
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    setItems(prev => [...prev, ...newItems]);

    // Auto-start in app mode — skip the intermediate "Ready to Score" screen
    if (shouldAutoStart) {
      setCurrentItemIndex(0);
      setScoringPhase('reach');
      setState('scoring');
    }
  }, [shouldAutoStart]);
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Sort by RICE score (highest first)
    const sortedItems = [...items]
      .filter(item => item.rice !== null)
      .sort((a, b) => (b.rice || 0) - (a.rice || 0));
    
    return sortedItems.map((item, idx) => ({
      id: sourceIdMap[item.id] || item.id,
      name: item.name,
      rank: idx + 1,
      score: item.rice || undefined,
      scoreBreakdown: {
        reach: item.reach || 0,
        impact: item.impact || 0,
        confidence: item.confidence || 0,
        effort: item.effort || 0,
      },
      metadata: {
        reach: item.reach,
        impact: item.impact,
        confidence: item.confidence,
        effort: item.effort,
      }
    }));
  }, [items, sourceIdMap]);

  const handleStart = (itemNames: string[]) => {
    const newItems: RiceItem[] = itemNames.map((name, index) => ({
      id: `item-${index}`,
      name,
      reach: null,
      impact: null,
      confidence: null,
      effort: null,
      rice: null,
    }));
    setItems(newItems);
    setCurrentItemIndex(0);
    setScoringPhase('reach');
    setState('scoring');
  };
  
  const handleScoreUpdate = (field: 'reach' | 'impact' | 'confidence' | 'effort', value: number | null) => {
    const updatedItems = [...items];
    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      [field]: value,
      // Worked out again at Finish, so a changed score is never submitted stale.
      rice: null,
    };
    setItems(updatedItems);
  };

  const handleNext = async () => {
    if (scoringPhase === 'reach') {
      setScoringPhase('impact');
    } else if (scoringPhase === 'impact') {
      setScoringPhase('confidence');
    } else if (scoringPhase === 'confidence') {
      setScoringPhase('effort');
    } else if (scoringPhase === 'effort') {
      // Move to next item or finish
      if (currentItemIndex < items.length - 1) {
        setCurrentItemIndex(currentItemIndex + 1);
        setScoringPhase('reach');
      } else {
        // Score via the canonical engine adapter (src/tools/rice.ts) — the
        // page collects inputs; the engine owns the RICE math.
        const scoreSet = await runRiceTool(
          items.map((item) => ({
            id: item.id,
            label: item.name,
            attributes: {
              reach: item.reach,
              impact: item.impact,
              confidence: item.confidence,
              effort: item.effort,
            },
          })),
          null,
          {}
        );
        const scoreById = new Map<string, number>(
          scoreSet.items.map((s) => [s.item_id as string, s.score as number])
        );
        setItems(items.map((item) => ({ ...item, rice: scoreById.get(item.id) ?? null })));
        if (isLiveSession) setLiveDone(true);
        else setState('results');
      }
    }
  };

  const handleBack = () => {
    if (scoringPhase === 'impact') {
      setScoringPhase('reach');
    } else if (scoringPhase === 'confidence') {
      setScoringPhase('impact');
    } else if (scoringPhase === 'effort') {
      setScoringPhase('confidence');
    } else if (scoringPhase === 'reach' && currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
      setScoringPhase('effort');
    }
  };

  const handleReset = () => {
    setState('input');
    setItems([]);
    setCurrentItemIndex(0);
    setScoringPhase('reach');
    setSourceIdMap({});
    setLiveDone(false);
  };

  const canProceed = () => {
    const current = items[currentItemIndex];
    if (!current) return false;
    if (scoringPhase === 'reach') return current.reach !== null && current.reach > 0;
    if (scoringPhase === 'impact') return current.impact !== null && current.impact > 0;
    if (scoringPhase === 'confidence') return current.confidence !== null && current.confidence >= 0 && current.confidence <= 100;
    if (scoringPhase === 'effort') return current.effort !== null && current.effort > 0;
    return false;
  };

  const currentItem = items[currentItemIndex];
  const allRiceReady = items.length > 0 && items.every(i => i.rice != null);
  const canSubmitLive = isLiveSession && allRiceReady;
  const phaseMeta = RICE_PHASE_META[scoringPhase];
  const phaseValue =
    currentItem &&
    (scoringPhase === 'reach'
      ? currentItem.reach
      : scoringPhase === 'impact'
        ? currentItem.impact
        : scoringPhase === 'confidence'
          ? currentItem.confidence
          : currentItem.effort);

  return (
    <ToolWrapper
      toolName="RICE Scoring"
      toolDescription="Prioritize using Reach, Impact, Confidence, and Effort"
      onImportItems={handleImportItems}
      onGetResults={canSubmitLive || state === 'results' ? handleGetResults : undefined}
      itemCount={items.length}
      {...props}
    >
      <div className="w-full">
        {/* Main Content */}
        <div>
          {state === 'input' && (
            <>
              {isAppMode && items.length === 0 && (
                <div className="mb-6 border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
                  <p className="text-sm text-[var(--text-sec)]">
                    {`Import items from your ${importSourceLabel} using the button in the header to begin RICE scoring.`}
                  </p>
                </div>
              )}
              {/* Standalone mode - show ItemInput */}
              {!isAppMode && <ItemInput onStart={handleStart} />}
            </>
          )}

          {state === 'scoring' && isLiveSession && liveDone && (
            <LiveToolScaffold
              title="RICE scoring"
              subtitle="Your ranking is ready to submit"
              progressDone={items.length * 4}
              progressTotal={items.length * 4}
            >
              <LiveToolComplete
                heading="All items scored"
                items={handleGetResults().map((r, i) => ({
                  id: r.id,
                  name: r.name,
                  rank: r.rank ?? i + 1,
                  detail: `RICE ${(r.score ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}`,
                }))}
                onChangeScores={() => setLiveDone(false)}
              />
            </LiveToolScaffold>
          )}

          {state === 'scoring' && currentItem && isLiveSession && !liveDone && (
            <LiveToolScaffold
              title="RICE scoring"
              subtitle="Reach → Impact → Confidence → Effort for each item"
              progressDone={
                currentItemIndex * 4 +
                (scoringPhase === 'reach' ? 0 : scoringPhase === 'impact' ? 1 : scoringPhase === 'confidence' ? 2 : 3)
              }
              progressTotal={items.length * 4}
            >
              <LiveSequentialScore
                itemName={currentItem.name}
                itemIndex={currentItemIndex}
                totalItems={items.length}
                phaseTitle={phaseMeta.title}
                valueDefinition={getAttributeByKey(scoringPhase)?.definition}
                phaseDescription={phaseMeta.description}
                placeholder={phaseMeta.placeholder}
                inputValue={phaseValue?.toString() ?? ''}
                onInputChange={v => {
                  const num = v.trim() === '' ? null : parseFloat(v);
                  handleScoreUpdate(scoringPhase, num != null && !isNaN(num) ? num : null);
                }}
                onNext={handleNext}
                onBack={canProceed() ? handleBack : undefined}
                canProceed={canProceed()}
                isLastStep={
                  scoringPhase === 'effort' && currentItemIndex === items.length - 1
                }
              />
            </LiveToolScaffold>
          )}

          {state === 'scoring' && currentItem && !isLiveSession && (
            <ScoringView
              item={currentItem}
              itemIndex={currentItemIndex}
              totalItems={items.length}
              phase={scoringPhase}
              onScoreUpdate={handleScoreUpdate}
              onNext={handleNext}
              onBack={handleBack}
              canProceed={canProceed()}
              progress={
                ((currentItemIndex * 4 +
                  (scoringPhase === 'reach' ? 0 : scoringPhase === 'impact' ? 1 : scoringPhase === 'confidence' ? 2 : 3)) /
                  (items.length * 4)) *
                100
              }
            />
          )}

          {state === 'results' && (
            <ResultsView 
              items={items} 
              onReset={handleReset}
            />
          )}
        </div>
      </div>
    </ToolWrapper>
  );
}

export default function RiceScoring(props: ToolHostProps) {
  return <RiceScoringContent {...props} />;
}
